import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../constants/queryKeys';
import { useWebSocket } from '../context/WebSocketContext';
import { clearWorkspaceReadCaches } from '../services/workspaceCache';

export interface OrchestrationEventPayload {
  id?: string;
  type?: string;
  source?: string;
  msmeId?: string;
  status?: string;
  triggeredAt?: string;
  payloadSummary?: Record<string, unknown>;
}

const WORKSPACE_EVENT_PREFIXES = [
  'document.',
  'orchestration.',
  'transactions.',
  'monitoring.',
] as const;

/** Host and agent telemetry. These are not ledger, bill, or profile changes. */
const TELEMETRY_ONLY_EVENT_TYPES = new Set([
  'monitoring.system_metrics',
  'monitoring.agent_metrics',
  'monitoring.workflow_metrics',
  'monitoring.anomalies',
  'monitoring.recommendations',
  'monitoring.started',
  'monitoring.stopped',
]);

const INVALIDATION_DEBOUNCE_MS = 1500;

export function shouldInvalidateWorkspace(
  event: OrchestrationEventPayload | null | undefined
): boolean {
  const type = String(event?.type || '').trim().toLowerCase();
  if (!type) {
    return false;
  }
  const source = String(event?.source || '').trim().toLowerCase();
  if (source === 'enhanced_monitoring' || TELEMETRY_ONLY_EVENT_TYPES.has(type)) {
    return false;
  }
  return WORKSPACE_EVENT_PREFIXES.some((prefix) => type.startsWith(prefix));
}

export function invalidateWorkspaceQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  clearWorkspaceReadCaches();
  void queryClient.invalidateQueries({ queryKey: queryKeys.msme.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.carbon.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.reporting.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.finance.overview });
}

export function useOrchestrationEvents(enabled = true): void {
  const { on, off } = useWebSocket();
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleOrchestrationEvent = (event: OrchestrationEventPayload) => {
      if (!shouldInvalidateWorkspace(event)) {
        return;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        invalidateWorkspaceQueries(queryClient);
      }, INVALIDATION_DEBOUNCE_MS);
    };

    on('orchestration:event', handleOrchestrationEvent);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      off('orchestration:event', handleOrchestrationEvent);
    };
  }, [enabled, on, off, queryClient]);
}
